import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { ReactNode, useState } from "react";
import { useTenantTranslation } from "hooks/formatters";
import { ImportWizardState, emptySearchState } from "./state";
import { Setter } from "hooks/state/syncing";
import SearchIcon from "@mui/icons-material/Search";
import { SearchSelector } from "components/search/SearchSelector";
export interface ImportWizardProps {
  importName: string;
  state: ImportWizardState;
  setState: Setter<ImportWizardState>;
  preview: ReactNode | null;
  topPanel?: ReactNode;
  onConfirm: (() => void) | null;
}

export const ImportWizard = ({
  importName,
  state,
  setState,
  preview,
  topPanel,
  onConfirm,
}: ImportWizardProps) => {
  const { t } = useTenantTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<SearchIcon />}
          onClick={() => {
            setState(emptySearchState);
            setOpen(true);
          }}
        >
          {t("importFromSearch")}
        </Button>
      </Stack>

      {open ? (
        <ImportWizardModal
          importName={importName}
          state={state}
          setState={setState}
          preview={preview}
          topPanel={topPanel}
          onConfirm={onConfirm}
          close={() => {
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
};

const ImportWizardModal = ({
  importName,
  state,
  setState,
  preview,
  topPanel,
  onConfirm,
  close,
}: ImportWizardProps & { close: () => void }) => {
  const { t } = useTenantTranslation();

  const dialogTitle = `${t("import")} ${importName} ${t("fromSearchSuffix")}`;

  return (
    <ImportWizardLayout
      title={dialogTitle}
      subtitle={topPanel}
      explorer={
        <SearchSelector
          selectedSearch={state.search}
          setSelectedSearch={(search) => {
            setState({ kind: "search", search });
          }}
        />
      }
      preview={
        state.search?.search_id && state.search.sessionId ? (
          (preview ?? <NoRelevantInformation />)
        ) : (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("chooseASearchToPreview")}
          </Typography>
        )
      }
      close={close}
      onConfirm={onConfirm}
    />
  );
};

export const ImportWizardLayout = ({
  title,
  subtitle,
  explorer,
  preview,
  close,
  onConfirm,
}: {
  title: string;
  subtitle?: ReactNode;
  explorer?: ReactNode;
  preview: ReactNode;
  close: () => void;
  onConfirm: (() => void) | null;
}) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();

  return (
    <Dialog
      open
      onClose={close}
      fullWidth
      maxWidth="lg"
      sx={{
        "& .MuiDialog-paper": {
          // Fix to always be a little less than "full height" so that the Input
          // Modal is still visible behind it
          height: "85vh",
        },
      }}
    >
      <DialogTitle>
        {title}
        {subtitle}
      </DialogTitle>

      <DialogContent>
        <Grid container columns={8} columnSpacing={3} sx={{ height: "100%" }}>
          {explorer !== undefined && (
            <Grid
              size={{ xs: 3 }}
              sx={{
                height: "100%",
                overflowY: "auto",
              }}
            >
              {explorer}
            </Grid>
          )}

          <Grid
            size={{ xs: explorer === undefined ? 8 : 5 }}
            sx={{
              height: "100%",
              overflowY: "auto",
            }}
          >
            <Box
              sx={{
                borderRadius: 1,
                backgroundColor: "grey.50",
                px: 4,
                py: 1,
                height: (theme) => `calc(100% - ${theme.spacing(2)})`,
                overflowY: "auto",
              }}
            >
              <Typography sx={{ mb: 1, color: theme.palette.text.secondary }}>
                {t("preview")}
              </Typography>

              {preview}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={close} color="secondary">
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={onConfirm == null}
          onClick={() => {
            if (onConfirm) {
              onConfirm();
            }
            close();
          }}
        >
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const NoRelevantInformation = () => {
  const { t } = useTenantTranslation();
  return <Typography>{t("noSpecifiedProduction")}</Typography>;
};
